import type { Icon, IconProps } from '@phosphor-icons/react'
import { forwardRef, type ReactNode } from 'react'
import { cn } from './primitives'

/*
 * One coloured SVG mark per legacy VSoft screen of Reports, Customer Service, Branches, Line Haul, Admin, Settings
 * and Fulfillment. Same grammar as OpsIcons: a 24-unit grid, a base shape saying what the screen holds (sheet, box,
 * building, phone, truck, gear…) and, where useful, a solid badge saying what you do there (search, plus, clock…).
 * Each is a Phosphor-compatible component, so it drops into PageHeader, PAGE_ICONS and the sidebar unchanged.
 */

const TONE = {
  green: '#16a34a', amber: '#d97706', blue: '#2563eb', red: '#dc2626', violet: '#7c3aed', teal: '#0d9488', pink: '#db2777', indigo: '#4f46e5',
  orange: '#ea580c', sky: '#0284c7', lime: '#65a30d', slate: '#475569', cyan: '#0891b2', rose: '#e11d48', emerald: '#059669', fuchsia: '#c026d3',
}

function mark(name: string, fg: string, draw: (fg: string) => ReactNode): Icon {
  const C = forwardRef<SVGSVGElement, IconProps>(function ReportIcon({ size = 24, className, weight: _w, mirrored, color: _c, alt, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} style={mirrored ? { transform: 'scale(-1, 1)' } : undefined} {...rest}>
        {draw(fg)}
      </svg>
    )
  })
  C.displayName = name
  return C as unknown as Icon
}

/* ------------------------------------------------------------ base shapes */

const sheet = (fg: string) => (
  <>
    <rect x="3.5" y="2.8" width="13.5" height="18.4" rx="2.2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M6.8 7.5h7M6.8 11h7M6.8 14.5h4.2" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
  </>
)
const grid = (fg: string) => (
  <>
    <rect x="3" y="3.5" width="15" height="15" rx="2.2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M3.5 8.5h14M3.5 13.5h14M9 4v14" stroke={fg} strokeWidth="1.4" />
  </>
)
const box = (fg: string) => (
  <>
    <path d="M11 2.8 18.4 6.5v9.6L11 19.8l-7.4-3.7V6.5L11 2.8Z" fill={fg} opacity=".15" />
    <path d="M11 2.8 18.4 6.5v9.6L11 19.8l-7.4-3.7V6.5L11 2.8Z" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M4 6.7 11 10.2l7-3.5M11 10.2v9.3" stroke={fg} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </>
)
const building = (fg: string) => (
  <>
    <path d="M3.5 20.5V6.2l7-3.2v17.5M10.5 8.5h6.5a1 1 0 0 1 1 1v11" fill={fg} opacity=".14" />
    <path d="M3.5 20.5V6.2l7-3.2v17.5M10.5 8.5h6.5a1 1 0 0 1 1 1v11M2 20.5h17.5" stroke={fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 9h2M6 12.5h2M6 16h2M13 12h2.5M13 15.5h2.5" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
  </>
)
const phone = (fg: string) => (
  <path d="M5.2 3.2h3.1l1.6 4.1-2 1.3a10 10 0 0 0 5.4 5.4l1.3-2 4.1 1.6v3.1a2 2 0 0 1-2.2 2A15.6 15.6 0 0 1 3.2 5.4a2 2 0 0 1 2-2.2Z" fill={fg} opacity=".18" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
)
const headset = (fg: string) => (
  <>
    <path d="M4 13v-2a7 7 0 0 1 14 0v2" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
    <rect x="2.8" y="12" width="4" height="6" rx="1.6" fill={fg} />
    <rect x="15.2" y="12" width="4" height="6" rx="1.6" fill={fg} />
    <path d="M17.2 18c0 1.8-2 2.8-5 2.8" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
  </>
)
const truck = (fg: string) => (
  <>
    <path d="M2.5 5.5h10v10h-10zM12.5 9h4l3 3.5v3h-7" fill={fg} opacity=".15" />
    <path d="M2.5 5.5h10v10h-10zM12.5 9h4l3 3.5v3h-7" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="6" cy="17" r="2" fill="#fff" stroke={fg} strokeWidth="1.8" />
    <circle cx="16" cy="17" r="2" fill="#fff" stroke={fg} strokeWidth="1.8" />
  </>
)
const gear = (fg: string) => (
  <>
    <path d="M11 2.8l1.6 1.9 2.4-.5.8 2.3 2.3.8-.5 2.4 1.9 1.6-1.9 1.6.5 2.4-2.3.8-.8 2.3-2.4-.5L11 19.2l-1.6-1.9-2.4.5-.8-2.3-2.3-.8.5-2.4L2.5 10.7l1.9-1.6-.5-2.4 2.3-.8.8-2.3 2.4.5L11 2.8Z" fill={fg} opacity=".15" stroke={fg} strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="11" cy="11" r="3" stroke={fg} strokeWidth="1.8" />
  </>
)
const person = (fg: string, x = 9, y = 7.5) => (
  <>
    <circle cx={x} cy={y} r="3.3" fill={fg} opacity=".2" stroke={fg} strokeWidth="1.8" />
    <path d={`M${x - 6} ${y + 12.5}a6 6 0 0 1 12 0`} fill={fg} opacity=".15" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
  </>
)
const pin = (fg: string) => (
  <>
    <path d="M11 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="11" cy="10" r="2.4" fill={fg} />
  </>
)
const chart = (fg: string) => (
  <>
    <path d="M3 20.5h16" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
    <rect x="4.5" y="11" width="3.4" height="7.5" rx="1" fill={fg} opacity=".35" />
    <rect x="9.3" y="6.5" width="3.4" height="12" rx="1" fill={fg} opacity=".6" />
    <rect x="14.1" y="3.5" width="3.4" height="15" rx="1" fill={fg} />
  </>
)
const chat = (fg: string) => (
  <path d="M3.5 5.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9.5l-4.2 3.3V15.5h0a2 2 0 0 1-1.8-2v-8Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
)
const tag = (fg: string) => (
  <>
    <path d="M3 11.2V4.3A1.3 1.3 0 0 1 4.3 3h6.9l8.3 8.3a1.3 1.3 0 0 1 0 1.8l-6.6 6.6a1.3 1.3 0 0 1-1.8 0L3 11.2Z" fill={fg} opacity=".15" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="7.4" cy="7.4" r="1.6" fill={fg} />
  </>
)

/* ----------------------------------------------------------------- badges */

const badge = (fg: string, glyph: ReactNode) => (
  <>
    <circle cx="17.6" cy="17.6" r="5" fill={fg} stroke="#fff" strokeWidth="1.2" />
    {glyph}
  </>
)
const W = { stroke: '#fff', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
const gSearch = <><circle cx="17" cy="17" r="1.9" {...W} /><path d="m18.4 18.4 1.5 1.5" {...W} /></>
const gPlus = <path d="M17.6 15.2v4.8M15.2 17.6h4.8" {...W} />
const gClock = <path d="M17.6 15.3v2.4l1.6 1" {...W} />
const gCheck = <path d="m15.4 17.7 1.5 1.5 2.9-3.1" {...W} />
const gPen = <path d="m15.6 19.6.5-1.9 2.6-2.6 1.4 1.4-2.6 2.6-1.9.5Z" fill="#fff" />
const gArrowOut = <path d="M15.4 17.6h4.2M18 15.8l1.8 1.8-1.8 1.8" {...W} />
const gArrowIn = <path d="M19.8 17.6h-4.2M17.4 15.8l-1.8 1.8 1.8 1.8" {...W} />
const gSwap = <path d="M15.4 16.4h4M18.4 15l1.2 1.4-1.2 1.4M19.8 18.9h-4M16.8 17.5l-1.2 1.4 1.2 1.4" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
const gUser = <><circle cx="17.6" cy="16.3" r="1.3" fill="#fff" /><path d="M15.5 20a2.1 2.1 0 0 1 4.2 0" {...W} /></>
const gList = <path d="M15.4 16h4.4M15.4 17.8h4.4M15.4 19.6h2.8" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
const gUp = <path d="M17.6 20v-4.6M15.7 17.2l1.9-1.9 1.9 1.9" {...W} />
const gBang = <><path d="M17.6 15.1v3" {...W} /><circle cx="17.6" cy="20" r=".8" fill="#fff" /></>
const gLock = <><rect x="15.6" y="17" width="4" height="3.2" rx=".6" fill="#fff" /><path d="M16.5 17v-1a1.1 1.1 0 0 1 2.2 0v1" stroke="#fff" strokeWidth="1.2" /></>
const gPlug = <path d="M16.3 15v1.6M18.9 15v1.6M15.6 16.6h4v1.2a2 2 0 0 1-4 0v-1.2ZM17.6 19.8v1" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />

/* ================================================================ reports */

export const AwbStatusTrackingIcon = mark('AwbStatusTrackingIcon', TONE.blue, (fg) => (
  <>
    <path d="M4 5.5h4M4 11h4M4 16.5h4" stroke={fg} strokeWidth="1.6" strokeLinecap="round" opacity=".5" />
    <circle cx="10.5" cy="5.5" r="2" fill={fg} />
    <circle cx="10.5" cy="11" r="2" fill={fg} opacity=".6" />
    <path d="M10.5 7.5v1.5" stroke={fg} strokeWidth="1.6" />
    <path d="M10.5 13v2" stroke={fg} strokeWidth="1.6" strokeDasharray="1.2 1.4" />
    {badge(fg, gSearch)}
  </>
))
export const DetailInquiryIcon = mark('DetailInquiryIcon', TONE.indigo, (fg) => <>{grid(fg)}{badge(fg, gSearch)}</>)
export const DataEntryInquiryIcon = mark('DataEntryInquiryIcon', TONE.teal, (fg) => <>{sheet(fg)}{badge(fg, gPen)}</>)
export const AwbEditHistoryIcon = mark('AwbEditHistoryIcon', TONE.amber, (fg) => <>{sheet(fg)}{badge(fg, gClock)}</>)
export const RunnerActivityIcon = mark('RunnerActivityIcon', TONE.green, (fg) => (
  <>
    {chart(fg)}
    {badge(fg, gCheck)}
  </>
))
export const TrackRoutesIcon = mark('TrackRoutesIcon', TONE.violet, (fg) => (
  <>
    <circle cx="5" cy="18" r="2.2" fill={TONE.green} />
    <circle cx="18.5" cy="5" r="2.2" fill={TONE.red} />
    <circle cx="9" cy="8" r="1.6" fill={fg} />
    <circle cx="14.5" cy="14" r="1.6" fill={TONE.amber} />
    <path d="M5 15.8c0-5 4-4 4-6.2M9 8c3 0 6 2 5.5 6M14.5 14c1.5-3.5 4-4.5 4-6.8" stroke={fg} strokeWidth="1.6" strokeLinecap="round" strokeDasharray="2 1.8" />
  </>
))
export const TrackRunnersIcon = mark('TrackRunnersIcon', TONE.sky, (fg) => (
  <>
    {pin(fg)}
    <path d="M17.5 3.5a4.5 4.5 0 0 1 3 3M16.8 6a2 2 0 0 1 1.3 1.3" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
  </>
))
export const PodManagementIcon = mark('PodManagementIcon', TONE.emerald, (fg) => (
  <>
    <path d="M2.8 6.5a1.5 1.5 0 0 1 1.5-1.5h4.3l1.6 2h7.5a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H4.3a1.5 1.5 0 0 1-1.5-1.5v-11Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    {badge(fg, gCheck)}
  </>
))
export const AuditReportsIcon = mark('AuditReportsIcon', TONE.rose, (fg) => (
  <>
    <path d="M11 2.5 18 5v5.5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5V5l7-2.5Z" fill={fg} opacity=".15" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M7.8 10.8 10 13l4-4.2" stroke={fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {badge(fg, gClock)}
  </>
))
export const CustomReportsIcon = mark('CustomReportsIcon', TONE.fuchsia, (fg) => <>{sheet(fg)}{badge(fg, gLock)}</>)
export const SlaReportIcon = mark('SlaReportIcon', TONE.orange, (fg) => (
  <>
    <path d="M3.5 16a8 8 0 1 1 15 0" fill={fg} opacity=".12" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
    <path d="M11 16 15 9.5" stroke={fg} strokeWidth="2" strokeLinecap="round" />
    <circle cx="11" cy="16" r="1.8" fill={fg} />
  </>
))
export const SuccessRateIcon = mark('SuccessRateIcon', TONE.lime, (fg) => (
  <>
    <circle cx="11" cy="11" r="8" fill={fg} opacity=".12" stroke={fg} strokeWidth="1.8" />
    <path d="M11 3a8 8 0 0 1 7.6 10.4L11 11V3Z" fill={fg} />
  </>
))
export const ClientReportsIcon = mark('ClientReportsIcon', TONE.cyan, (fg) => <>{chart(fg)}{badge(fg, gUser)}</>)
export const BranchReportsIcon = mark('BranchReportsIcon', TONE.slate, (fg) => <>{building(fg)}{badge(fg, gList)}</>)
export const FinancialReportsIcon = mark('FinancialReportsIcon', TONE.emerald, (fg) => (
  <>
    {chart(fg)}
    {badge(fg, <path d="M18.6 15.5h-1.5a1 1 0 0 0 0 2h1a1 1 0 0 1 0 2h-1.6M17.6 14.8v.7M17.6 19.5v.7" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />)}
  </>
))

/* ======================================================= customer service */

export const CallCenterPlanIcon = mark('CallCenterPlanIcon', TONE.violet, (fg) => (
  <>
    <rect x="3" y="4.5" width="15" height="14.5" rx="2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M3.5 9h14M7.5 2.8v3.4M13.5 2.8v3.4" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
    {badge(fg, gPlus)}
  </>
))
export const CurrentCallsIcon = mark('CurrentCallsIcon', TONE.green, (fg) => (
  <>
    {phone(fg)}
    <path d="M13.5 3.5a5.5 5.5 0 0 1 5 5M13.3 6.6a2.6 2.6 0 0 1 2.1 2.1" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
  </>
))
export const CallCenterInquiryIcon = mark('CallCenterInquiryIcon', TONE.blue, (fg) => <>{phone(fg)}{badge(fg, gSearch)}</>)
export const CsTypesIcon = mark('CsTypesIcon', TONE.amber, (fg) => <>{tag(fg)}{badge(fg, gList)}</>)
export const CsStatusesIcon = mark('CsStatusesIcon', TONE.teal, (fg) => <>{tag(fg)}{badge(fg, gCheck)}</>)
export const CustomerServiceTicketsIcon = mark('CustomerServiceTicketsIcon', TONE.pink, (fg) => <>{headset(fg)}{badge(fg, gList)}</>)
export const SupportTicketsIcon = mark('SupportTicketsIcon', TONE.orange, (fg) => (
  <>
    <path d="M3 7a1.5 1.5 0 0 1 1.5-1.5h13A1.5 1.5 0 0 1 19 7v1.8a2 2 0 0 0 0 4V14.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 3 14.5v-1.7a2 2 0 0 0 0-4V7Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M12.5 6v9.5" stroke={fg} strokeWidth="1.5" strokeDasharray="1.5 1.5" />
    {badge(fg, gBang)}
  </>
))
export const ShipmentNotesIcon = mark('ShipmentNotesIcon', TONE.sky, (fg) => <>{chat(fg)}{badge(fg, gPen)}</>)

/* ================================================================ branches */

export const ManageBranchesIcon = mark('ManageBranchesIcon', TONE.slate, (fg) => <>{building(fg)}{badge(fg, gPen)}</>)
export const SendToBranchIcon = mark('SendToBranchIcon', TONE.blue, (fg) => <>{building(fg)}{badge(fg, gArrowOut)}</>)
export const ReceiveFromBranchIcon = mark('ReceiveFromBranchIcon', TONE.green, (fg) => <>{building(fg)}{badge(fg, gArrowIn)}</>)
export const SettleTransferIcon = mark('SettleTransferIcon', TONE.emerald, (fg) => <>{box(fg)}{badge(fg, gCheck)}</>)
export const TransferInquiryIcon = mark('TransferInquiryIcon', TONE.indigo, (fg) => <>{box(fg)}{badge(fg, gSwap)}</>)

/* =============================================================== line haul */

export const HaulRoutesIcon = mark('HaulRoutesIcon', TONE.violet, (fg) => (
  <>
    <circle cx="5" cy="17.5" r="2.2" stroke={fg} strokeWidth="1.8" fill={fg} fillOpacity=".2" />
    <circle cx="17" cy="5" r="2.2" stroke={fg} strokeWidth="1.8" fill={fg} fillOpacity=".2" />
    <path d="M7.2 17.5H14a3 3 0 0 0 0-6H8a3 3 0 0 1 0-6h6.8" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
  </>
))
export const TripsIcon = mark('TripsIcon', TONE.orange, (fg) => <>{truck(fg)}{badge(fg, gPlus)}</>)
export const VehiclesIcon = mark('VehiclesIcon', TONE.cyan, (fg) => truck(fg))

/* =================================================================== admin */

export const CompanyProfileIcon = mark('CompanyProfileIcon', TONE.indigo, (fg) => <>{building(fg)}{badge(fg, gCheck)}</>)
export const EmployeesIcon = mark('EmployeesIcon', TONE.teal, (fg) => (
  <>
    {person(fg, 8.5, 7.5)}
    <circle cx="16" cy="6.5" r="2.4" stroke={fg} strokeWidth="1.6" opacity=".7" />
    <path d="M17 11.5a5 5 0 0 1 3.8 4.8" stroke={fg} strokeWidth="1.6" strokeLinecap="round" opacity=".7" />
  </>
))
export const UsersBranchesAuthIcon = mark('UsersBranchesAuthIcon', TONE.rose, (fg) => (
  <>
    <rect x="3" y="3" width="15" height="15" rx="2.2" fill={fg} opacity=".12" stroke={fg} strokeWidth="1.8" />
    <path d="m5.8 7.3 1.2 1.2 2-2.1M5.8 12.8l1.2 1.2 2-2.1M11.5 7.5h3.5M11.5 13h3.5" stroke={fg} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    {badge(fg, gLock)}
  </>
))
export const IntegrationDataIcon = mark('IntegrationDataIcon', TONE.lime, (fg) => (
  <>
    <ellipse cx="10.5" cy="5.5" rx="7" ry="2.7" fill={fg} opacity=".18" stroke={fg} strokeWidth="1.8" />
    <path d="M3.5 5.5v11c0 1.5 3.1 2.7 7 2.7M17.5 5.5V11M3.5 11c0 1.5 3.1 2.7 7 2.7" stroke={fg} strokeWidth="1.8" />
    {badge(fg, gPlug)}
  </>
))

/* ================================================================ settings */

export const ManageStatusIcon = mark('ManageStatusIcon', TONE.blue, (fg) => (
  <>
    <path d="M5 21V3.5M5 4h11l-2.2 3.8L16 11.6H5" fill={fg} opacity=".16" />
    <path d="M5 21V3.5M5 4h11l-2.2 3.8L16 11.6H5" stroke={fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {badge(fg, gPen)}
  </>
))
export const ReasonsIcon = mark('ReasonsIcon', TONE.amber, (fg) => (
  <>
    <path d="M11 3.2 19.5 18a1 1 0 0 1-.9 1.5H3.4a1 1 0 0 1-.9-1.5L11 3.2Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M11 9v4.5" stroke={fg} strokeWidth="2" strokeLinecap="round" />
    <circle cx="11" cy="16.3" r="1.1" fill={fg} />
  </>
))
export const RecipientIcon = mark('RecipientIcon', TONE.pink, (fg) => <>{person(fg)}{badge(fg, gCheck)}</>)
export const ZonesIcon = mark('ZonesIcon', TONE.emerald, (fg) => (
  <>
    <circle cx="11" cy="11" r="8.3" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M2.8 11h16.4M11 2.8c2.8 2.6 2.8 13.8 0 16.4M11 2.8c-2.8 2.6-2.8 13.8 0 16.4" stroke={fg} strokeWidth="1.4" />
    {badge(fg, <path d="M17.6 20.4s-2-1.7-2-3.3a2 2 0 0 1 4 0c0 1.6-2 3.3-2 3.3Z" fill="#fff" />)}
  </>
))
export const CellsIcon = mark('CellsIcon', TONE.orange, (fg) => (
  <>
    <rect x="3" y="3" width="6.5" height="6.5" rx="1.4" fill={fg} />
    <rect x="11.5" y="3" width="6.5" height="6.5" rx="1.4" fill={fg} opacity=".35" />
    <rect x="3" y="11.5" width="6.5" height="6.5" rx="1.4" fill={fg} opacity=".35" />
    <rect x="11.5" y="11.5" width="6.5" height="6.5" rx="1.4" fill={fg} opacity=".6" />
  </>
))
export const MessageTemplatesIcon = mark('MessageTemplatesIcon', TONE.violet, (fg) => (
  <>
    {chat(fg)}
    <path d="M7 8h8M7 11h5" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
  </>
))
export const DefineRoutesIcon = mark('DefineRoutesIcon', TONE.teal, (fg) => (
  <>
    {pin(fg)}
    {badge(fg, gPen)}
  </>
))
export const GeneralSettingsIcon = mark('GeneralSettingsIcon', TONE.slate, (fg) => gear(fg))

/* ============================================================= fulfillment */

export const FulfillmentSummaryIcon = mark('FulfillmentSummaryIcon', TONE.indigo, (fg) => (
  <>
    <rect x="3" y="3" width="16" height="16" rx="2.2" fill={fg} opacity=".12" stroke={fg} strokeWidth="1.8" />
    <path d="M6.5 15v-3M10 15V8M13.5 15v-5M17 15V6.5" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
  </>
))
export const ProductDataIcon = mark('ProductDataIcon', TONE.amber, (fg) => <>{box(fg)}{badge(fg, gPen)}</>)
export const ReceivingItemsIcon = mark('ReceivingItemsIcon', TONE.green, (fg) => <>{box(fg)}{badge(fg, gArrowIn)}</>)
export const SalesOrdersIcon = mark('SalesOrdersIcon', TONE.blue, (fg) => (
  <>
    <path d="M3 4h2.4l2 10h10l2-7H6.4" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="8.5" cy="18.3" r="1.6" fill={fg} />
    <circle cx="15.5" cy="18.3" r="1.6" fill={fg} />
  </>
))
export const ItemsStockIcon = mark('ItemsStockIcon', TONE.teal, (fg) => (
  <>
    <rect x="3" y="12.5" width="7.5" height="6.5" rx="1.2" fill={fg} opacity=".6" />
    <rect x="11.5" y="12.5" width="7.5" height="6.5" rx="1.2" fill={fg} opacity=".35" />
    <rect x="7.2" y="4.5" width="7.5" height="7" rx="1.2" fill={fg} />
  </>
))
export const ItemCardIcon = mark('ItemCardIcon', TONE.pink, (fg) => (
  <>
    <rect x="2.8" y="4.5" width="16.4" height="13" rx="2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M6 9h4M6 12.5h6.5" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    <path d="m12.8 10.6 1.6-1.6 1.5 1.3 1.8-2" stroke={fg} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </>
))
export const ImportExcelIcon = mark('ImportExcelIcon', TONE.lime, (fg) => <>{sheet(fg)}{badge(fg, gUp)}</>)

/** Every screen mark by page id (navigation + page headers). */
export const REPORT_ICONS = {
  // reports
  'reports.awbStatus': AwbStatusTrackingIcon,
  'reports.details': DetailInquiryIcon,
  'reports.dataEntry': DataEntryInquiryIcon,
  'reports.awbEdit': AwbEditHistoryIcon,
  'reports.pod': PodManagementIcon,
  'reports.runnerActivity': RunnerActivityIcon,
  'reports.trackRoutes': TrackRoutesIcon,
  'reports.trackRunners': TrackRunnersIcon,
  'reports.performance': SlaReportIcon,
  'reports.successRate': SuccessRateIcon,
  'reports.audit': AuditReportsIcon,
  'reports.clients': ClientReportsIcon,
  'reports.branches': BranchReportsIcon,
  'reports.financial': FinancialReportsIcon,
  'reports.custom': CustomReportsIcon,
  // customer service
  'cs.callPlan': CallCenterPlanIcon,
  'cs.currentCalls': CurrentCallsIcon,
  'cs.callInquiry': CallCenterInquiryIcon,
  'cs.csTypes': CsTypesIcon,
  'cs.csStatuses': CsStatusesIcon,
  'cs.tickets': CustomerServiceTicketsIcon,
  'cs.complaints': SupportTicketsIcon,
  'cs.notes': ShipmentNotesIcon,
  // branches
  'branches.manage': ManageBranchesIcon,
  'branches.send': SendToBranchIcon,
  'branches.receive': ReceiveFromBranchIcon,
  'branches.settle': SettleTransferIcon,
  'branches.inquiry': TransferInquiryIcon,
  // line haul
  'linehaul.routes': HaulRoutesIcon,
  'linehaul.history': TripsIcon,
  'linehaul.vehicles': VehiclesIcon,
  // admin
  'admin.employees': EmployeesIcon,
  'admin.usersBranches': UsersBranchesAuthIcon,
  'admin.integrationData': IntegrationDataIcon,
  // settings
  'settings.company': CompanyProfileIcon,
  'settings.statuses': ManageStatusIcon,
  'settings.deliveryReasons': ReasonsIcon,
  'settings.returnReasons': ReasonsIcon,
  'settings.recipient': RecipientIcon,
  'settings.zones': ZonesIcon,
  'settings.cells': CellsIcon,
  'settings.notifications': MessageTemplatesIcon,
  'settings.defineRoutes': DefineRoutesIcon,
  'settings.branches': ManageBranchesIcon,
  // fulfillment
  'fulfillment.summary': FulfillmentSummaryIcon,
  'fulfillment.products': ProductDataIcon,
  'fulfillment.receiving': ReceivingItemsIcon,
  'fulfillment.orders': SalesOrdersIcon,
  'fulfillment.itemsStock': ItemsStockIcon,
  'fulfillment.itemCard': ItemCardIcon,
  'fulfillment.importExcel': ImportExcelIcon,
} as const satisfies Record<string, Icon>
